import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/AppLayout"
import { fmtHours } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import { IconCalendar, IconChartBar, IconList, IconUpload, IconX } from "@tabler/icons-react"
import DayCalendar from "../../TimeTracking/components/DayCalendar"
import ReportsView, { type Report } from "../../TimeTracking/components/ReportsView"

interface TimeEntry {
  id: number
  user_id: number
  user_name: string
  user_avatar: string | null
  project_id: number | null
  project_name: string
  customer_name: string
  description: string
  entry_date: string
  hours: number | null
  started_at: string | null
  stopped_at: string | null
  billable: boolean
  running: boolean
  timer_display: string
  duration_hours: number | null
}

interface ProjectOption {
  id: number
  name: string
  customer_id: number
  customer_name: string
}

interface UserOption {
  id: number
  name: string
  project_ids: number[]
}

interface CustomerOption {
  id: number
  name: string
}

interface Filters {
  from?: string
  to?: string
  project_id?: number
  user_id?: number
  customer_id?: number
}

interface Props {
  [key: string]: unknown
  tab: "list" | "calendar" | "reports"
  time_entries: TimeEntry[]
  pagy: PagyMeta | null
  filters: Filters
  projects: ProjectOption[]
  users: UserOption[]
  customers: CustomerOption[]
  calendar_date: string | null
  week_start: string | null
  report: Report | null
}

function formatDate(dateStr: string) {
  const date = new Date(`${dateStr}T00:00:00`)
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function formatHours(hours: number | null) {
  if (hours === null) return "—"
  return fmtHours(hours)
}

export default function AdminTimeTrackingIndex() {
  const { tab, time_entries, pagy, filters, projects, users, customers, calendar_date, week_start, report } =
    usePage<Props>().props

  function buildParams(overrides: Record<string, string | undefined> = {}) {
    const merged: Record<string, string | undefined> = {
      tab,
      from: filters.from,
      to: filters.to,
      project_id: filters.project_id ? String(filters.project_id) : undefined,
      user_id: filters.user_id ? String(filters.user_id) : undefined,
      customer_id: filters.customer_id ? String(filters.customer_id) : undefined,
      ...(calendar_date ? { date: calendar_date } : {}),
      ...overrides,
    }
    return Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== undefined && v !== ""))
  }

  function switchTab(newTab: string) {
    router.get("/admin/time_tracking", buildParams({ tab: newTab }), { preserveState: true })
  }

  function applyFilters(overrides: Record<string, string | undefined>) {
    router.get("/admin/time_tracking", buildParams(overrides), { preserveState: true })
  }

  function clearFilters() {
    router.get("/admin/time_tracking", { tab }, { preserveState: true })
  }

  const hasFilters = filters.from || filters.to || filters.project_id || filters.user_id || filters.customer_id

  const pdfQuery = new URLSearchParams()
  if (filters.from) pdfQuery.set("from", filters.from)
  if (filters.to) pdfQuery.set("to", filters.to)
  if (filters.user_id) pdfQuery.set("user_id", String(filters.user_id))
  if (filters.project_id) pdfQuery.set("project_id", String(filters.project_id))
  if (filters.customer_id) pdfQuery.set("customer_id", String(filters.customer_id))
  const pdfUrl = `/admin/time_tracking/pdf?${pdfQuery.toString()}`

  return (
    <>
      <Head title="Admin · Time Tracking - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Admin · Time Tracking</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold">Admin · Time Tracking</h1>
            <Tabs value={tab} onValueChange={switchTab}>
              <TabsList>
                <TabsTrigger value="list">
                  <IconList className="mr-1 h-4 w-4" />
                  List
                </TabsTrigger>
                <TabsTrigger value="calendar">
                  <IconCalendar className="mr-1 h-4 w-4" />
                  Calendar
                </TabsTrigger>
                <TabsTrigger value="reports">
                  <IconChartBar className="mr-1 h-4 w-4" />
                  Reports
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href="/time_entries/import">
                <IconUpload className="mr-1 h-4 w-4" />
                Import CSV
              </a>
            </Button>
          </div>
        </div>

        {tab === "list" && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="date"
                value={filters.from || ""}
                onChange={(e) => applyFilters({ from: e.target.value || undefined })}
                className="w-auto"
              />
              <Input
                type="date"
                value={filters.to || ""}
                onChange={(e) => applyFilters({ to: e.target.value || undefined })}
                className="w-auto"
              />
              <Select
                value={filters.user_id ? String(filters.user_id) : ""}
                onValueChange={(v) => applyFilters({ user_id: v || undefined })}
              >
                <SelectTrigger size="sm">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.project_id ? String(filters.project_id) : ""}
                onValueChange={(v) => applyFilters({ project_id: v || undefined })}
              >
                <SelectTrigger size="sm">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.customer_id ? String(filters.customer_id) : ""}
                onValueChange={(v) => applyFilters({ customer_id: v || undefined })}
              >
                <SelectTrigger size="sm">
                  <SelectValue placeholder="All Customers" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <IconX className="mr-1 h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {time_entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-sm">{formatDate(entry.entry_date)}</TableCell>
                    <TableCell className="text-sm">{entry.user_name}</TableCell>
                    <TableCell>
                      <div>
                        <span className="text-sm font-medium">{entry.project_name}</span>
                        <p className="text-muted-foreground text-xs">{entry.customer_name}</p>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-sm">{entry.description}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatHours(entry.hours)}</TableCell>
                    <TableCell>
                      {entry.running ? (
                        <Badge variant="default">Running</Badge>
                      ) : entry.billable ? (
                        <Badge variant="outline">Billable</Badge>
                      ) : (
                        <Badge variant="secondary">Non-billable</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {time_entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground text-center">
                      {hasFilters ? "No entries match your filters." : "No time entries yet."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {pagy && <PagyPagination pagy={pagy} />}
          </>
        )}

        {tab === "calendar" && (
          <DayCalendar
            entries={time_entries}
            calendarDate={calendar_date || new Date().toISOString().split("T")[0]}
            weekStart={week_start || calendar_date || new Date().toISOString().split("T")[0]}
            projects={projects.map((p) => ({ id: p.id, name: p.name, customer_name: p.customer_name }))}
            isAdmin
            readOnly
            basePath="/admin/time_tracking"
            navParams={{
              tab: "calendar",
              from: filters.from,
              to: filters.to,
              project_id: filters.project_id,
              user_id: filters.user_id,
              customer_id: filters.customer_id,
            }}
          />
        )}

        {tab === "reports" && report && (
          <ReportsView
            report={report}
            filters={filters}
            users={users}
            projects={projects}
            customers={customers}
            onApplyFilters={applyFilters}
            onClearFilters={clearFilters}
            pdfUrl={pdfUrl}
          />
        )}
      </div>
    </>
  )
}

AdminTimeTrackingIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
